import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Phone } from "lucide-react";
import { api } from "@/lib/api";
import { addDays, dayLabel, mondayOf, runsOn, todayIso } from "@/lib/dates";
import { Input } from "@/components/ui/input";
import { SwapHistory } from "@/components/rota/SwapHistory";
import { backLabel, offLabel } from "@/lib/constants";
import { useAuth } from "@/context/AuthContext";
import { WaitingJobs } from "@/components/jobs/WaitingJobs";
import { ControlExpiryBanner } from "@/components/RiderDocs";

const vehicleState = (v, d) => {
  if (v.status === "off_road") return [`${offLabel(v)} · ${backLabel(v)}`, "text-red-300 border-red-500/40"];
  const run = d.open_runs.find((r) => r.bike_id === v.id);
  if (run) return [`Out · ${run.rider_name}`, "text-sky-300 border-sky-500/40"];
  const b = d.bookings.filter((x) => x.vehicle_id === v.id);
  if (b.length) return [b.map((x) => `${x.user_name} ${x.start.slice(11)}–${x.end.slice(11)}`).join(", "), "text-amber-300 border-amber-500/40"];
  return ["Available", "text-emerald-300 border-emerald-500/40"];
};

const DayView = ({ day }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/control/day", { params: { day } }).then((r) => setD(r.data)); }, [day]);
  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const phone = (id) => d.members.find((m) => m.id === id)?.phone;
  return (
    <div className="space-y-6">
      <div className="grid gap-4 lg:grid-cols-3" data-testid="control-day">
        {d.areas.filter((a) => a.active).map((a) => (
          <section key={a.id} className="space-y-3 rounded-2xl border bg-card p-4" style={{ borderTop: `5px solid ${a.color || "#FEDC19"}` }}>
            <h3 className="area-text text-lg font-extrabold" style={{ color: a.color }}>{a.name}</h3>
            {d.templates.filter((t) => t.active && runsOn(t, day)).map((t) => {
              const slots = d.slots.filter((s) => s.area_id === a.id && s.template_id === t.id);
              const live = d.now.slice(0, 10) === day && d.now.slice(11) >= t.start && d.now.slice(11) < t.end;
              return (
                <div key={t.id} className={`rounded-xl border p-3 ${live ? "border-primary ring-1 ring-primary" : ""} ${slots.length >= t.requirements.reduce((x, r) => x + r.count, 0) ? "bg-emerald-500/[0.06]" : slots.length ? "bg-amber-500/[0.06]" : "bg-red-500/[0.06]"}`}>
                  <div className="mb-2 flex justify-between text-sm font-bold"><span>{t.name}{live && <span className="ml-2 rounded bg-primary px-1.5 text-[10px] text-primary-foreground">ON NOW</span>}</span><span className="font-mono text-xs text-muted-foreground">{t.start}–{t.end}</span></div>
                  {t.requirements.map((r) => {
                    const list = slots.filter((s) => s.position === r.key);
                    return (
                      <div key={r.key} className="space-y-1 text-sm">
                        {list.map((s) => <div key={s.id} className={`flex justify-between gap-2 rounded px-2 py-0.5 ${r.key === "controller" ? "bg-sky-500/15" : "bg-yellow-400/15"}`}><span>{r.label}: <b>{s.user_name}</b>{s.vehicle_name ? ` · ${s.vehicle_name}` : ""}</span>{phone(s.user_id) && <a href={`tel:${phone(s.user_id)}`} className="text-primary"><Phone className="inline h-3.5 w-3.5" /></a>}</div>)}
                        {Array.from({ length: Math.max(0, r.count - list.length) }).map((_, i) => <div key={i} className="font-semibold text-red-400">{r.label}: UNFILLED</div>)}
                      </div>
                    );
                  })}
                </div>
              );
            })}
          </section>
        ))}
      </div>
      <div className="grid gap-6 lg:grid-cols-12">
        <section className="overflow-hidden rounded-2xl border bg-card lg:col-span-8" data-testid="control-vehicles">
          <h3 className="border-b px-5 py-3 font-bold">Vehicles by area</h3>
          {[...d.areas, { id: null, name: "No area", color: "#6B7280" }].map((a) => {
            const list = d.vehicles.filter((v) => (v.area_id || null) === a.id);
            if (!list.length) return null;
            return (
              <div key={a.id || "none"}>
                <div className="area-text px-5 py-2 text-xs font-bold uppercase tracking-widest" style={{ color: a.color, background: `${a.color}1A` }}>{a.name}</div>
                <div className="grid gap-px bg-border sm:grid-cols-2">
                  {list.map((v) => { const [label, cls] = vehicleState(v, d); return (
                    <Link key={v.id} to={`/fleet/bikes/${v.id}`} className="flex items-center justify-between gap-2 bg-card px-4 py-2.5 text-sm hover:bg-secondary/50">
                      <span className="font-semibold"><span className={`mr-2 rounded px-1.5 py-0.5 text-[10px] font-bold uppercase ${v.type === "car" ? "bg-cyan-500/20 text-cyan-300" : "bg-yellow-400/20 text-yellow-300"}`}>{v.type === "car" ? "Car" : "Bike"}</span>{v.name} <span className="text-xs font-normal text-muted-foreground">{v.base}</span></span>
                      <span className={`truncate rounded-full border px-2 py-0.5 text-[11px] ${cls}`}>{label}</span>
                    </Link>
                  ); })}
                </div>
              </div>
            );
          })}
        </section>
        <section className="rounded-2xl border bg-card p-5 lg:col-span-4" data-testid="control-unavailable">
          <h3 className="mb-2 font-bold">Unavailable today</h3>
          {d.unavailable.length === 0 ? <p className="text-sm text-muted-foreground">Nobody.</p> : d.unavailable.map((u) => <div key={u.id} className="text-sm">{u.user_name} <span className="text-xs text-muted-foreground">{u.note}</span></div>)}
        </section>
      </div>
    </div>
  );
};

const WeekView = ({ start }) => {
  const [d, setD] = useState(null);
  useEffect(() => { api.get("/rota", { params: { start, days: 7 } }).then((r) => setD(r.data)); }, [start]);
  if (!d) return <div className="h-40 animate-pulse rounded-2xl bg-card" />;
  const days = Array.from({ length: 7 }, (_, i) => addDays(start, i));
  return (
    <section className="overflow-x-auto rounded-2xl border bg-card" data-testid="control-week">
      <table className="w-full min-w-[900px] text-sm">
        <thead><tr className="border-b text-left text-xs text-muted-foreground"><th className="px-4 py-3">Area / shift</th>{days.map((x) => <th key={x} className="px-2 py-3">{dayLabel(x)}</th>)}</tr></thead>
        <tbody>
          {d.areas.filter((a) => a.active).flatMap((a) => d.templates.filter((t) => t.active).map((t) => {
            const need = t.requirements.reduce((s, r) => s + r.count, 0);
            return (
              <tr key={a.id + t.id} className="border-b">
                <td className="px-4 py-2" style={{ borderLeft: `4px solid ${a.color || "#FEDC19"}` }}><b>{a.name}</b> · {t.name}</td>
                {days.map((x) => {
                  if (!runsOn(t, x)) return <td key={x} className="px-2 py-2 text-muted-foreground">—</td>;
                  const n = d.slots.filter((s) => s.date === x && s.area_id === a.id && s.template_id === t.id).length;
                  return <td key={x} className="px-2 py-2"><span className={`rounded-full px-2 py-0.5 font-mono text-xs ${n >= need ? "bg-emerald-500/15 text-emerald-300" : n ? "bg-amber-500/15 text-amber-300" : "bg-red-500/15 text-red-300"}`}>{n}/{need}</span></td>;
                })}
              </tr>
            );
          }))}
        </tbody>
      </table>
    </section>
  );
};

export default function Control() {
  const { user } = useAuth();
  const [view, setView] = useState("day");
  const views = ["admin", "controller"].includes(user.role) ? ["day", "week", "swaps"] : ["day", "week"];
  const [day, setDay] = useState(todayIso());
  return (
    <div className="space-y-6">
      {(user.permissions?.jobs || 0) >= 3 && <WaitingJobs />}
      <ControlExpiryBanner />
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-primary">Duty Controller</p>
          <h1 className="mt-1 text-3xl font-extrabold tracking-tight sm:text-4xl">Control room</h1>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          {views.map((v) => <button key={v} onClick={() => setView(v)} data-testid={`control-view-${v}`} className={`rounded-full border px-4 py-1.5 text-sm font-semibold capitalize ${view === v ? "border-primary bg-primary text-primary-foreground" : "text-muted-foreground"}`}>{v}</button>)}
          {view !== "swaps" && <Input type="date" value={day} onChange={(e) => e.target.value && setDay(e.target.value)} className="w-[160px]" data-testid="control-date-input" />}
          <Link to="/dispatch" className="rounded-full bg-destructive px-4 py-1.5 text-sm font-bold text-white" data-testid="control-dispatch-link">Dispatch board</Link>
          <Link to="/rota" className="rounded-full border px-4 py-1.5 text-sm" data-testid="control-rota-link">{(user.permissions?.rota || 0) >= 2 ? "Edit rota" : "View rota"}</Link>
        </div>
      </div>
      {view === "day" && <DayView day={day} />}
      {view === "week" && <WeekView start={mondayOf(day)} />}
      {view === "swaps" && <SwapHistory />}
    </div>
  );
}
